import React, { useState } from 'react';
import { Shield, Check, X, Settings, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { notifyPermissionsUpdated } from '../../hooks/usePermissions';

const PermissionsManagement = () => {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState('staff');
  const [editablePermissions, setEditablePermissions] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customPermissions, setCustomPermissions] = useState({});

  const availableRoles = [
    { id: 'super_admin', name: 'Super Admin', color: 'bg-purple-100 text-purple-800', icon: '👑' },
    { id: 'admin',       name: 'Admin',       color: 'bg-red-100 text-red-800',    icon: '🛠️' },
    { id: 'manager',     name: 'Manager',     color: 'bg-blue-100 text-blue-800',  icon: '📋' },
    { id: 'staff',       name: 'Staff',       color: 'bg-green-100 text-green-800',icon: '👤' },
    { id: 'guest',       name: 'Guest',       color: 'bg-gray-100 text-gray-800',  icon: '👥' }
  ];

  const permissionTypes = [
    { id: 'view',    name: 'View',    icon: '👁️', color: 'text-blue-600'   },
    { id: 'create',  name: 'Create',  icon: '➕', color: 'text-green-600'  },
    { id: 'edit',    name: 'Edit',    icon: '✏️', color: 'text-yellow-600' },
    { id: 'delete',  name: 'Delete',  icon: '🗑️', color: 'text-red-600'    },
    { id: 'approve', name: 'Approve', icon: '✅', color: 'text-purple-600' }
  ];

  const systemPages = [
    { id: 'dashboard',               name: 'Dashboard',               description: 'Main overview with statistics',                  category: 'Core',      icon: '📊' },
    { id: 'login',                   name: 'Login',                   description: 'Authentication page',                            category: 'Auth',      icon: '🔐' },
    { id: 'user-management',         name: 'User Management',         description: 'Create and manage user accounts',                category: 'Admin',     icon: '👥' },
    { id: 'permissions-management',  name: 'Permissions',             description: 'Role-based access control',                      category: 'Admin',     icon: '🛡️' },
    { id: 'material-management',     name: 'Gemstone & Materials',    description: 'Manage jewelry materials and prices',            category: 'Admin',     icon: '💎' },
    { id: 'category-management',     name: 'Product Categories',      description: 'Manage jewelry categories',                      category: 'Admin',     icon: '📂' },
    { id: 'vendor-management',       name: 'Vendor Details',          description: 'Vendor information and contacts',                category: 'Admin',     icon: '🏢' },
    { id: 'vendor-ledger',           name: 'Vendor Ledger',           description: 'Vendor transaction ledger',                      category: 'Admin',     icon: '📒' },
    { id: 'accounting-summary',      name: 'Accounting Summary',      description: 'Vendor-wise accounting and balances',            category: 'Admin',     icon: '🧾' },
    { id: 'billing-settings',        name: 'Billing Settings',        description: 'Configure billing defaults',                     category: 'Admin',     icon: '⚙️' },
    { id: 'vendor-import',           name: 'Vendor Import',           description: 'Import vendor stock from Excel',                 category: 'Admin',     icon: '📥' },
    { id: 'cost-breakdown',          name: 'Cost Breakdown',          description: 'View how an item\'s acquisition cost is calculated', category: 'Admin', icon: '🧮' },
    { id: 'upload-profiles',         name: 'Upload Profiles',         description: 'Manage bulk inventory upload profiles',          category: 'Admin',     icon: '📤' },
    { id: 'view-inventory',          name: 'View Inventory',          description: 'Browse and search inventory',                    category: 'Inventory', icon: '👁️' },
    { id: 'add-inventory',           name: 'Add Inventory',           description: 'Add new jewelry pieces',                         category: 'Inventory', icon: '➕' },
    { id: 'edit-inventory',          name: 'Edit Inventory',          description: 'Modify existing jewelry pieces',                 category: 'Inventory', icon: '✏️' },
    { id: 'inventory-pictures',      name: 'Inventory Pictures',      description: 'Manage jewelry item photos',                     category: 'Inventory', icon: '🖼️' },
    { id: 'missing-images',          name: 'Missing Photos',          description: 'Find inventory items without photos',            category: 'Inventory', icon: '🖼️' },
    { id: 'inventory-upload',        name: 'Import from Excel',       description: 'Import inventory from Excel spreadsheets',       category: 'Inventory', icon: '📥' },
    { id: 'indian-billing',          name: 'Indian Billing',          description: 'Generate bills for Indian market',               category: 'Billing',   icon: '🧾' },
    { id: 'us-billing',              name: 'US Billing',              description: 'Generate bills for US market',                   category: 'Billing',   icon: '💵' },
    { id: 'estimate-cart',           name: 'Multi-Item Billing',      description: 'Shopping cart for billing multiple items at once', category: 'Billing',   icon: '🛒' },
    { id: 'billing-history',         name: 'Billing History',         description: 'View past billing records',                      category: 'Billing',   icon: '🗂️' },
    { id: 'jewelry-returns',         name: 'Jewelry Returns',         description: 'Process jewelry return transactions',            category: 'Billing',   icon: '↩️' },
    { id: 'inr-estimate',            name: 'INR Estimate',            description: 'Create estimates in Indian Rupees',              category: 'Estimates', icon: '💰' },
    { id: 'usd-estimate',            name: 'USD Estimate',            description: 'Create estimates in US Dollars',                 category: 'Estimates', icon: '💵' },
    { id: 'view-estimates',          name: 'View Estimates',          description: 'Browse and manage all estimates',                category: 'Estimates', icon: '📋' },
    { id: 'sales-report',            name: 'Sales Report',            description: 'Sales performance analytics',                    category: 'Reports',   icon: '📊' },
    { id: 'available-stock',         name: 'Available Stock',         description: 'Current stock levels',                           category: 'Reports',   icon: '📦' },
    { id: 'sold-items',              name: 'Sold Items Report',       description: 'History of sold jewelry items',                  category: 'Reports',   icon: '✅' },
    { id: 'item-aging',              name: 'Item Aging',              description: 'Track how long items have been in stock',        category: 'Reports',   icon: '⏳' },
    { id: 'vendor-stock',            name: 'Vendor Stock',            description: 'Stock grouped by vendor',                        category: 'Reports',   icon: '🏪' },
    { id: 'customer-list',           name: 'Customer List',           description: 'View and manage customers',                      category: 'Reports',   icon: '👥' },
    { id: 'exhibition-reconciliation', name: 'Reconciliation (Web)', description: 'Web reconciliation reports',                    category: 'Reports',   icon: '🔄' },
    { id: 'manual-rate-entry',       name: 'Rate Manager',            description: 'Manage gold and dollar rates',                   category: 'Utilities', icon: '💱' },
    { id: 'barcode-generator',       name: 'Barcode Generator',       description: 'Generate and print barcodes',                    category: 'Utilities', icon: '📊' },
    { id: 'catalog-settings',        name: 'Customer Catalog',        description: 'Manage the customer-facing catalog',             category: 'Utilities', icon: '📖' },
    { id: 'reconciliation',          name: 'Reconciliation',          description: 'Scan and reconcile items at exhibitions',        category: 'Mobile',    icon: '📱' },
    { id: 'scheme-management',       name: 'Scheme Management',       description: 'Manage jewelry saving schemes',                  category: 'Schemes',   icon: '💼' },
    { id: 'scheme-enroll',           name: 'Scheme Enrollment',       description: 'Enroll customers into schemes',                  category: 'Schemes',   icon: '📝' },
    { id: 'scheme-payment',          name: 'Scheme Payments',         description: 'Record and manage scheme payments',              category: 'Schemes',   icon: '💳' },
    { id: 'scheme-dashboard',        name: 'Scheme Dashboard',        description: 'Overview of active schemes',                     category: 'Schemes',   icon: '📊' },
    { id: 'scheme-redemption',       name: 'Scheme Redemption',       description: 'Process scheme maturity and redemptions',        category: 'Schemes',   icon: '🎁' },
    { id: 'scheme-reports',          name: 'Scheme Reports',          description: 'Analytics for jewelry schemes',                  category: 'Schemes',   icon: '📈' }
  ];

  const getPageAccess = (pageId, role) => {
    const none = { create: false, view: false, edit: false, delete: false, approve: false };
    switch (pageId) {
      case 'dashboard':
      case 'login':
        return { ...none, view: true };
      case 'user-management':
      case 'permissions-management':
        return role === 'super_admin' ? { create: true, view: true, edit: true, delete: true, approve: true } : none;
      case 'material-management':
        if (role === 'super_admin') return { create: true, view: true, edit: true, delete: true, approve: true };
        if (role === 'admin')       return { create: true, view: true, edit: true, delete: true, approve: false };
        if (role === 'manager')     return { ...none, view: true, edit: true };
        if (role === 'staff')       return { ...none, view: true };
        return none;
      case 'category-management':
      case 'vendor-management':
        if (['super_admin', 'admin'].includes(role)) return { create: true, view: true, edit: true, delete: true, approve: false };
        if (role === 'manager') return { create: true, view: true, edit: true, delete: false, approve: false };
        return none;
      case 'add-inventory':
      case 'edit-inventory':
        if (['super_admin', 'admin'].includes(role)) return { create: true, view: true, edit: true, delete: true, approve: true };
        if (role === 'manager') return { create: true, view: true, edit: true, delete: false, approve: false };
        return none;
      case 'view-inventory':
      case 'available-stock':
      case 'vendor-stock':
        if (['super_admin', 'admin', 'manager'].includes(role)) return { create: true, view: true, edit: true, delete: false, approve: false };
        return { ...none, view: true };
      case 'indian-billing':
      case 'us-billing':
        if (['super_admin', 'admin'].includes(role)) return { create: true, view: true, edit: true, delete: true, approve: true };
        if (role === 'manager') return { create: true, view: true, edit: false, delete: false, approve: false };
        return none;
      case 'view-estimates':
        if (['super_admin', 'admin', 'manager', 'staff'].includes(role)) return { ...none, view: true };
        return none;
      case 'inr-estimate':
      case 'usd-estimate':
      case 'estimate-cart':
        if (['super_admin', 'admin', 'manager'].includes(role)) return { create: true, view: true, edit: true, delete: false, approve: false };
        return none;
      case 'manual-rate-entry':
        if (['super_admin', 'admin'].includes(role)) return { create: true, view: true, edit: true, delete: false, approve: false };
        if (role === 'manager') return { ...none, view: true };
        return none;
      case 'barcode-generator':
        if (['super_admin', 'admin', 'manager', 'staff'].includes(role)) return { ...none, view: true };
        return none;
      default:
        if (role === 'super_admin') return { create: true, view: true, edit: true, delete: true, approve: true };
        if (role === 'admin')       return { ...none, view: true, edit: true };
        if (role === 'manager')     return { ...none, view: true };
        return none;
    }
  };

  const getPermissionMatrix = () => {
    const matrix = {};
    systemPages.forEach(page => {
      matrix[page.id] = {};
      availableRoles.forEach(role => {
        matrix[page.id][role.id] = getPageAccess(page.id, role.id);
      });
    });
    return matrix;
  };

  const handlePermissionChange = (pageId, role, permType, value) => {
    setEditablePermissions(prev => ({
      ...prev,
      [pageId]: { ...prev[pageId], [role]: { ...prev[pageId]?.[role], [permType]: value } }
    }));
    setHasChanges(true);
  };

  const cancelEditing = () => { setIsEditing(false); setHasChanges(false); setEditablePermissions({}); };

  const groupedPages = systemPages.reduce((acc, page) => {
    if (!acc[page.category]) acc[page.category] = [];
    acc[page.category].push(page);
    return acc;
  }, {});

  const defaultMatrix = getPermissionMatrix();

  React.useEffect(() => { loadCustomPermissions(); }, []);

  React.useEffect(() => {
    if (!isEditing) {
      setEditablePermissions(mergePermissions(getPermissionMatrix(), customPermissions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPermissions]);

  const loadCustomPermissions = async () => {
    try {
      setLoading(true);
      if (typeof api.getCustomPermissions === 'function') {
        const res = await api.getCustomPermissions();
        // api.getCustomPermissions() returns the permissions object directly
        // (handleResponse unwraps { success, data } → returns data)
        setCustomPermissions(res || {});
      } else {
        setCustomPermissions({});
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const mergePermissions = (defaults, overrides) => {
    const merged = { ...defaults };
    Object.keys(overrides).forEach(pageId => {
      if (!merged[pageId]) return;
      Object.keys(overrides[pageId]).forEach(role => {
        const bp = overrides[pageId][role];
        merged[pageId][role] = {
          view:    bp.view    || false,
          create:  bp.create  || false,
          edit:    bp.edit    || false,
          delete:  bp.delete  || false,
          approve: bp.approve || false
        };
      });
    });
    return merged;
  };

  const savePermissions = async () => {
    try {
      setLoading(true);
      const backend = {};
      Object.keys(editablePermissions).forEach(pageId => {
        backend[pageId] = {};
        Object.keys(editablePermissions[pageId]).forEach(role => {
          const p = editablePermissions[pageId][role];
          backend[pageId][role] = {
            view:    p.view    || false,
            create:  p.create  || false,
            edit:    p.edit    || false,
            delete:  p.delete  || false,
            approve: p.approve || false
          };
        });
      });
      const res = await api.saveCustomPermissions(backend);
      if (res.success) {
        setCustomPermissions(backend);
        setHasChanges(false);
        setIsEditing(false);
        notifyPermissionsUpdated();
      } else {
        throw new Error(res.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving permissions:', err);
      alert('Error saving permissions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Reset all permissions to default? Custom settings will be removed.')) return;
    try {
      setLoading(true);
      const res = await api.resetPermissions();
      if (res.success) {
        setCustomPermissions({});
        setEditablePermissions(defaultMatrix);
        setHasChanges(false);
        setIsEditing(false);
        notifyPermissionsUpdated();
      } else {
        throw new Error(res.message || 'Failed to reset');
      }
    } catch (err) {
      console.error('Error resetting permissions:', err);
      alert('Error resetting permissions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPermissions = () =>
    Object.keys(editablePermissions).length > 0 ? editablePermissions : defaultMatrix;

  // Bulk action helpers
  const applyBulk = (buildPerms) => {
    const updated = { ...editablePermissions };
    systemPages.forEach(page => {
      if (!updated[page.id]) updated[page.id] = {};
      updated[page.id][selectedRole] = buildPerms();
    });
    setEditablePermissions(updated);
    setHasChanges(true);
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <Shield className="mx-auto h-12 w-12 text-red-400 mb-3" />
          <h3 className="text-lg font-semibold text-red-800 mb-1">Access Denied</h3>
          <p className="text-red-600 text-sm">Only Super Administrators can access this page.</p>
        </div>
      </div>
    );
  }

  const currentPerms = getCurrentPermissions();
  const selectedRoleLabel = availableRoles.find(r => r.id === selectedRole)?.name;

  return (
    /* Fixed frame that covers the content area (sidebar is w-16 = left-16) */
    <div className="fixed left-16 top-0 right-0 bottom-0 z-10 flex flex-col bg-gray-50">
      <PageIdentifier pageId={SCREEN_IDS?.ADMINISTRATION?.PERMISSIONS || 'ADM-002'} pageName="Permissions Management" />

      {/* ── Always-visible control bar ── */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm px-6 py-3">
        {/* Row 1: title + action buttons */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">User Permissions</h1>
              <p className="text-xs text-gray-500">Configure screen access per role</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEditing && hasChanges && (
              <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                <Info className="w-3 h-3" /> Unsaved changes
              </span>
            )}
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Settings className="w-4 h-4" /> Edit Permissions
              </button>
            ) : (
              <>
                <button
                  onClick={cancelEditing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button
                  onClick={savePermissions}
                  disabled={!hasChanges || loading}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    hasChanges && !loading
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Check className="w-4 h-4" /> {loading ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Row 2: role tabs + bulk actions */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {availableRoles.map(role => (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                  selectedRole === role.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span>{role.icon}</span>
                {role.name}
              </button>
            ))}
          </div>

          {isEditing && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-xs text-gray-400 font-medium">Bulk:</span>
              <button
                onClick={() => applyBulk(() => ({ create: false, view: true,  edit: false, delete: false, approve: false }))}
                className="px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
              >View Only</button>
              <button
                onClick={() => applyBulk(() => ({ create: true, view: true,  edit: true,  delete: false, approve: false }))}
                className="px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-full hover:bg-green-100 transition-colors"
              >Create/Edit</button>
              <button
                onClick={() => applyBulk(() => ({ create: true, view: true,  edit: true,  delete: true,  approve: true }))}
                className="px-2.5 py-1 text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-full hover:bg-purple-100 transition-colors"
              >Full Access</button>
              <button
                onClick={() => applyBulk(() => ({ create: false, view: false, edit: false, delete: false, approve: false }))}
                className="px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-full hover:bg-red-100 transition-colors"
              >No Access</button>
              <button
                onClick={resetToDefault}
                className="px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors"
              >Reset Default</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Single scroll zone — both axes scroll here so sticky top-0 and sticky left-0 both resolve to this container ── */}
      <div className="flex-1 overflow-auto">

        {/* Table: no intermediate overflow wrappers so sticky positioning works in both directions */}
        <table className="min-w-full border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-800">
              {/* Corner cell: sticky in BOTH directions */}
              <th className="sticky left-0 z-30 px-5 py-3.5 bg-slate-800 min-w-[280px] border-r border-slate-700/60 text-left">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-300 uppercase tracking-wider">
                  <span>📄</span> Screen / Feature
                  <span className="ml-1 text-slate-500 font-normal normal-case tracking-normal">
                    — {selectedRoleLabel}
                  </span>
                </div>
              </th>
              {permissionTypes.map(pt => (
                <th key={pt.id} className="px-6 py-3.5 text-center min-w-[110px] border-r border-slate-700/30 last:border-r-0">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-lg leading-none">{pt.icon}</span>
                    <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">{pt.name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {Object.entries(groupedPages).map(([category, pages]) => (
              <React.Fragment key={category}>
                {/* Category separator */}
                <tr>
                  <td
                    colSpan={permissionTypes.length + 1}
                    className="px-5 py-2 bg-slate-50 border-y border-slate-200 sticky left-0"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-4 bg-blue-500 rounded-full flex-shrink-0"></span>
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{category}</span>
                      <span className="text-xs text-slate-400">({pages.length})</span>
                    </div>
                  </td>
                </tr>

                {pages.map((page, idx) => {
                  const pp = currentPerms[page.id]?.[selectedRole] || getPageAccess(page.id, selectedRole);
                  return (
                    <tr
                      key={page.id}
                      className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                    >
                      {/* Frozen first column */}
                      <td className="sticky left-0 z-10 bg-inherit px-5 py-3 border-r border-gray-200">
                        <div className="flex items-center gap-3">
                          <span className="text-base leading-none flex-shrink-0">{page.icon}</span>
                          <div>
                            <div className="text-sm font-medium text-gray-800">{page.name}</div>
                            <div className="text-xs text-gray-400 mt-0.5 max-w-[210px] truncate">{page.description}</div>
                          </div>
                        </div>
                      </td>

                      {permissionTypes.map(pt => {
                        const has = pp[pt.id] || false;
                        return (
                          <td key={pt.id} className="px-6 py-3 text-center border-r border-gray-100 last:border-r-0">
                            {isEditing ? (
                              <input
                                type="checkbox"
                                checked={has}
                                onChange={e => handlePermissionChange(page.id, selectedRole, pt.id, e.target.checked)}
                                title={`${pt.name} — ${page.name}`}
                                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                              />
                            ) : (
                              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                                has ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-300'
                              }`}>
                                {has ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* Summary bar — below table, scrolls with content */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-0 divide-x divide-gray-200">
            <div className="text-center px-5">
              <div className="text-2xl font-bold text-gray-900">{systemPages.length}</div>
              <div className="text-xs text-gray-500 mt-0.5">Total Screens</div>
            </div>
            {permissionTypes.map(pt => {
              const count = systemPages.filter(page => {
                const pp = currentPerms[page.id]?.[selectedRole] || getPageAccess(page.id, selectedRole);
                return pp[pt.id];
              }).length;
              return (
                <div key={pt.id} className="text-center px-5">
                  <div className={`text-2xl font-bold ${pt.color}`}>{count}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{pt.name}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend — view mode only */}
        {!isEditing && (
          <div className="px-6 pb-8">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-5 py-3 flex items-center gap-2">
                <Info className="w-4 h-4 text-white/70" />
                <h3 className="text-sm font-bold text-white">Permission Types & Role Guide</h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-5 gap-3">
                  {permissionTypes.map(pt => (
                    <div key={pt.id} className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-2xl">{pt.icon}</span>
                      <h4 className="font-semibold text-gray-800 text-sm mt-2 mb-1">{pt.name}</h4>
                      <p className="text-xs text-gray-500">
                        {pt.id === 'view'    && 'Read and browse content'}
                        {pt.id === 'create'  && 'Add new records'}
                        {pt.id === 'edit'    && 'Modify existing records'}
                        {pt.id === 'delete'  && 'Remove records permanently'}
                        {pt.id === 'approve' && 'Workflow sign-off'}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {availableRoles.map(role => (
                    <div key={role.id} className="bg-white p-3 rounded-lg border-l-4 border-blue-400 border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span>{role.icon}</span>
                        <span className="font-medium text-gray-800 text-sm">{role.name}</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {role.id === 'super_admin' && 'Complete system control'}
                        {role.id === 'admin'       && 'Administrative privileges'}
                        {role.id === 'manager'     && 'Management capabilities'}
                        {role.id === 'staff'       && 'Standard staff access'}
                        {role.id === 'guest'       && 'Limited read-only access'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>{/* end single scroll zone */}
    </div>
  );
};

export default PermissionsManagement;
